import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaUser, FaLock, FaArrowRight, FaEye, FaEyeSlash } from 'react-icons/fa'
import BuzzTicketLogo from '../assets/BuzzTicketLogo.png'
import TermsAndConditionsModal from './TermsAndConditionsModal'
import PrivacyPolicyModal from './PrivacyPolicyModal'
import './css/loginpopup.css'

function LoginPopup({
  onClose,
  isLogin,
  setIsLogin,
  username,
  setUsername,
  password,
  setPassword,
  errorMessage,
  loading,
  handleSubmit,
  handleRegister,
  handleToggle,
  isRegisterButtonDisabled,
  showLoginToggle = true
}) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeRefundPolicy, setAgreeRefundPolicy] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const openTermsModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTermsModal(true);
  };

  const openPrivacyModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrivacyModal(true);
  };

  const renderLoginForm = () => (
    <div className="form-container">
      <h2 className="form-title">Welcome Back</h2>
      <p className="form-subtitle">Please sign in to your account</p>
      
      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="auth-form">
        <div className="input-group">
          <FaUser className="input-icon" />
          <input
            type="email"
            placeholder="Email Address"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="form-input"
            required
          />
        </div>
        
        <div className="input-group">
          <FaLock className="input-icon" />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="form-input"
            required
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
        
        <button
          type="submit"
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
          <FaArrowRight style={{ marginLeft: '8px' }} />
        </button>
      </form>

      <div className="form-bottom">
        <a
          href="#"
          className="forgot-password-link"
          onClick={(e) => { e.preventDefault(); onClose(); navigate('/forgot-password'); }}
        >
          Forgot password?
        </a>

        {showLoginToggle && (
          <p className="bottom-text">
            Don't have an account?{' '}
            <button
              onClick={handleToggle}
              className="toggle-button"
            >
              Sign up
            </button>
          </p>
        )}
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="form-container">
      <h2 className="form-title">Join BuzzTicket</h2>
      <p className="form-subtitle">Create your account to get started</p>
      
      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
      
      <form
        onSubmit={(e) =>
          handleRegister(e, { termsAccepted: agreeTerms, privacyAccepted: agreeRefundPolicy })
        }
        className="auth-form"
      >
        <div className="input-group">
          <FaUser className="input-icon" />
          <input
            type="email"
            placeholder="Email Address"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="form-input"
            required
          />
        </div>
        
        <div className="input-group">
          <FaLock className="input-icon" />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="form-input"
            required
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
        
        <div className="consent-group">
          <label className="consent-checkbox">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
            />
            <span>
              I have read and agree to the BuzzTickets{' '}
              <span
                className="consent-link"
                role="button"
                tabIndex={0}
                onClick={openTermsModal}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openTermsModal(e);
                }}
              >
                Terms and Conditions
              </span>{' '}
              and{' '}
              <span
                className="consent-link"
                role="button"
                tabIndex={0}
                onClick={openPrivacyModal}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openPrivacyModal(e);
                }}
              >
                Privacy Policy
              </span>
              .
            </span>
          </label>

          <label className="consent-checkbox">
            <input
              type="checkbox"
              checked={agreeRefundPolicy}
              onChange={(e) => setAgreeRefundPolicy(e.target.checked)}
            />
            <span>
              I understand that BuzzTickets is a ticketing platform only and is not responsible for
              organizing events or issuing refunds. Refunds and event-related disputes must be handled
              directly with the event organizer.
            </span>
          </label>
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={isRegisterButtonDisabled || !agreeTerms || !agreeRefundPolicy}
        >
          Register
        </button>
      </form>
      
      <div className="form-bottom">
        <p className="bottom-text">
          Already have an account?{' '}
          <button 
            onClick={handleToggle}
            className="toggle-button"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );

  return (
    <div className="login-popup-overlay" onClick={onClose}>
      <div className="login-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-popup" onClick={onClose}>
          &times;
        </button>
        <div className="popup-logo-container">
          <img src={BuzzTicketLogo} alt="BuzzTicket Logo" className="popup-logo" />
        </div>
        <div className="auth-form-container">
          {isLogin ? renderLoginForm() : renderRegisterForm()}
        </div>
      </div>

      {showTermsModal && (
        <TermsAndConditionsModal onClose={() => setShowTermsModal(false)} />
      )}

      {showPrivacyModal && (
        <PrivacyPolicyModal onClose={() => setShowPrivacyModal(false)} />
      )}
    </div>
  );
}

export default LoginPopup
